import { Link } from "react-router-dom";
import { useCourses } from "@/hooks/useCourses";
import { motion } from "motion/react";

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

export default function CourseSection() {
  const { courses } = useCourses();
  const displayCourses = courses.slice(0, 4);

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={sectionVariants}
      className="w-full py-16 md:py-20 px-4 md:px-6 bg-white"
    >
      <div className="max-w-[1280px] mx-auto">
        {/* Section Header */}
        <motion.div variants={fadeUpVariants} className="text-center mb-12">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full mb-3">
            교육과정
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-3">
            초보자를 위한 강의 과정
          </h2>
          <p className="text-sm text-[#8C8780] max-w-[500px] mx-auto">
            무료 입문부터 실전 투자까지, 체계적인 커리큘럼으로 경매의 모든 것을
            배워보세요
          </p>
        </motion.div>

        {/* Course Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {displayCourses.map((course) => (
            <motion.div
              key={course.id}
              variants={cardVariants}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <Link
                to={`/courses/${course.id}`}
                className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all block"
              >
                {/* Thumbnail */}
                <div className="relative h-44 overflow-hidden">
                  <img loading="lazy" decoding="async"
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                        course.type === "무료"
                          ? "bg-emerald-500 text-white"
                          : course.type === "실전"
                          ? "bg-rose-500 text-white"
                          : course.type === "오프라인"
                          ? "bg-violet-500 text-white"
                          : "bg-[#C4956A] text-white"
                      }`}
                    >
                      {course.type}
                    </span>
                  </div>
                  {course.type !== "무료" && (
                    <div className="absolute bottom-3 right-3">
                      <span className="px-3 py-1 text-sm font-bold bg-white/90 rounded-full text-[#1C1917]">
                        {course.price.toLocaleString()}원
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-[#1C1917] mb-2 line-clamp-2 leading-snug">
                    {course.title}
                  </h3>
                  <p className="text-xs text-[#8C8780] mb-3 line-clamp-2 leading-relaxed">
                    {course.description}
                  </p>

                  <div className="flex items-center justify-between text-xs text-[#8C8780] pt-3 border-t border-[#F0EDE8]">
                    <span className="flex items-center gap-1">
                      <i className="ri-video-line"></i>
                      {course.lectureCount}강 · {course.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {course.students.toLocaleString()}명
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" as const }}
          className="text-center mt-10"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#1C1917] text-white text-sm font-medium rounded-full hover:bg-[#C4956A] transition-all"
            >
              모든 강의 보기
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
}